import { useEffect, useMemo, useState } from 'react'
import Alert from '../../ui-components/Alert'
import { useQuery } from '@tanstack/react-query'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { fetchAlerts } from '../../../repository/MutualFundDetail'
import { AlertsResponseType } from '../../../types/mutual-fund-detail.types'
import { AlertsTypes } from '../../../types/mutual-fund-detail.types'

type AlertConfigType = {
  [id in `${AlertsTypes}`]: {
    title: string
    caption: string
  }
}

const alertConfig: AlertConfigType = {
  [AlertsTypes.OUT_OF_FORM_ALERT]: {
    title: 'Out-of-form Alert',
    caption: `Affecting portfolio performance`
  },
  [AlertsTypes.REGULAR_FUND_ALERT]: {
    title: 'Regular fund Alert',
    caption: `You’re losing money on extra commissions`
  }
}

type AlertDatumType = {
  id: `${AlertsTypes}`
  title: string
  caption: string
}

export default function Alerts() {
  const [alerts, setAlerts] = useState<AlertDatumType[]>([])
  const { isin, data: metadata, investmentData } = useMetaDataContext()

  const isNoInvestments = useMemo(() => {
    return !investmentData?.investedAmount || investmentData?.investedAmount?.amount <= 0
  }, [investmentData])

  const purchaseMode = useMemo(() => {
    return metadata?.fundDetails?.purchaseMode ?? ''
  }, [metadata?.fundDetails?.purchaseMode])

  const { data } = useQuery<any, Error>({
    queryKey: ['alerts', isin, purchaseMode],
    queryFn: () => {
      return fetchAlerts(isin, purchaseMode)
    },
    enabled: !isNoInvestments && !!purchaseMode,
    retry: false
  })

  useEffect(() => {
    if (data?.data) {
      const dataCopy = data?.data as AlertsResponseType
      setAlerts(() => {
        return (dataCopy?.listOfAlerts ?? [])?.map((alertType) => {
          return {
            id: alertType,
            title: alertConfig?.[alertType]?.title,
            caption: alertConfig?.[alertType]?.caption
          }
        })
      })
    }
  }, [data])

  const handleAlertClose = (id: `${AlertsTypes}`) => {
    setAlerts((alertsPrev) => {
      return alertsPrev?.filter((datum) => datum?.id !== id)
    })
  }

  if (isNoInvestments || alerts?.length <= 0) return null

  return (
    <div className='w-full flex flex-col animate-fade-750 rounded-t-xl overflow-hidden mt-[-3px]'>
      {alerts?.map(({ id, title, caption }, idx) => {
        return (
          <Alert
            key={id}
            id={id}
            title={title}
            caption={caption}
            onAlertClose={handleAlertClose}
            onLearnMore={() => null}
            isBorderTop={idx !== 0}
          />
        )
      })}
    </div>
  )
}
